import { createRouter, createWebHistory } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'

// Import views
import LoginView from '../views/LoginView.vue'
import DashboardView from '../views/DashboardView.vue'
import PatronsView from '../views/PatronsView.vue'
import PatronDetailView from '../views/PatronDetailView.vue'
import DonationsView from '../views/DonationsView.vue'
import DonationDetailView from '../views/DonationDetailView.vue'
import ExpensesView from '../views/ExpensesView.vue'
import ExpenseDetailView from '../views/ExpenseDetailView.vue'
import DailyStatsView from '../views/DailyStatsView.vue'
import ProgramsView from '../views/ProgramsView.vue'
import ActivityLogView from '../views/ActivityLogView.vue'
import SettingsView from '../views/SettingsView.vue'
import MessageHistoryView from '../views/MessageHistoryView.vue'
import BankAccountsView from '../views/BankAccountsView.vue'
import TransfersView from '../views/TransfersView.vue'
import ReceiptPaymentView from '../views/ReceiptPaymentView.vue'
import BeneficiariesView from '../views/BeneficiariesView.vue'
import BeneficiaryDetailView from '../views/BeneficiaryDetailView.vue'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true },
  },
  {
    path: '/patrons',
    name: 'patrons',
    component: PatronsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/patrons/:id',
    name: 'patron-detail',
    component: PatronDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/donations',
    name: 'donations',
    component: DonationsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/donations/:id',
    name: 'donation-detail',
    component: DonationDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/expenses',
    name: 'expenses',
    component: ExpensesView,
    meta: { requiresAuth: true },
  },
  {
    path: '/expenses/:id',
    name: 'expense-detail',
    component: ExpenseDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/programs',
    name: 'programs',
    component: ProgramsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/activity-log',
    name: 'activity-log',
    component: ActivityLogView,
    meta: { requiresAuth: true },
  },
  {
    // backward-compat redirect
    path: '/daily-stats',
    redirect: '/activity-log',
  },
  {
    path: '/daily-stats-legacy',
    name: 'daily-stats',
    component: DailyStatsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/messages',
    name: 'messages',
    component: MessageHistoryView,
    meta: { requiresAuth: true },
  },
  {
    path: '/bank-accounts',
    name: 'bank-accounts',
    component: BankAccountsView,
    meta: { requiresAuth: true },
  },
  {
    path: '/transfers',
    name: 'transfers',
    component: TransfersView,
    meta: { requiresAuth: true },
  },
  {
    path: '/beneficiaries',
    name: 'beneficiaries',
    component: BeneficiariesView,
    meta: { requiresAuth: true },
  },
  {
    path: '/beneficiaries/:id',
    name: 'beneficiary-detail',
    component: BeneficiaryDetailView,
    meta: { requiresAuth: true },
  },
  {
    path: '/commitments',
    name: 'commitments',
    component: () => import('../views/CommitmentsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/renewals',
    name: 'renewals',
    component: () => import('../views/RenewalsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/receipt-payment',
    name: 'receipt-payment',
    component: ReceiptPaymentView,
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/form-10be',
    name: 'form-10be',
    component: () => import('../views/Form10BEView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/income-expenditure',
    name: 'income-expenditure',
    component: () => import('../views/IncomeExpenditureView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/balance-sheet',
    name: 'balance-sheet',
    component: () => import('../views/BalanceSheetView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/reports/integrity-check',
    name: 'integrity-check',
    component: () => import('../views/IntegrityCheckView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/bank-reconciliation',
    name: 'bank-reconciliation',
    component: () => import('../views/ReconciliationView.vue'),
    meta: { requiresAuth: true },
  },
  // ── Receipts (Universal Receipt System) ──
  {
    path: '/receipts',
    name: 'receipts',
    component: () => import('../views/ReceiptsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/receipts/new',
    name: 'receipt-create',
    component: () => import('../views/ReceiptCreateView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/receipts/:id',
    name: 'receipt-detail',
    component: () => import('../views/ReceiptDetailView.vue'),
    meta: { requiresAuth: true },
  },
  // ── Receipt Settings ──
  {
    path: '/settings/receipt-types',
    name: 'receipt-types',
    component: () => import('../views/ReceiptTypesView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/funds',
    name: 'funds',
    component: () => import('../views/FundsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings/membership-tiers',
    name: 'membership-tiers',
    component: () => import('../views/MembershipTiersView.vue'),
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Scroll the main content area to top, not the whole page
    // IntercomLayout uses a scrollable main area
    const main = document.querySelector('main') || document.querySelector('.intercom-main')
    if (main) {
      main.scrollTop = 0
      return false // prevent default window scroll
    }
    return { top: 0 }
  },
})

// Navigation guard - check authentication
router.beforeEach(async (to, from, next) => {
  const { isAuthenticated, fetchMe, user } = useAuth()
  const { apiClient } = await import('@shared/utils/apiClient.js')

  // Going to login page - always allow
  if (to.name === 'login') {
    // If already authenticated, redirect to dashboard
    if (isAuthenticated.value && user.value) {
      next({ name: 'dashboard' })
      return
    }
    next()
    return
  }

  // Route requires auth
  if (to.meta.requiresAuth) {
    // Check if token exists
    if (!apiClient.isAuthenticated()) {
      next({ name: 'login', query: { redirect: to.fullPath } })
      return
    }

    // Token exists, fetch user info if not loaded
    if (!user.value) {
      try {
        await fetchMe()
      } catch (error) {
        // Failed to fetch user (token invalid) - redirect to login
        apiClient.clearToken()
        next({ name: 'login', query: { redirect: to.fullPath } })
        return
      }
    }
  }

  next()
})

export default router
